export interface DroppedUploadFile {
  file: File;
  relativePath: string;
}

export interface DroppedUploadPayload {
  files: DroppedUploadFile[];
  directories: string[];
}

interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
}

interface FileSystemFileEntryLike extends FileSystemEntryLike {
  file: (success: (file: File) => void, error?: (error: DOMException) => void) => void;
}

interface FileSystemDirectoryReaderLike {
  readEntries: (
    success: (entries: FileSystemEntryLike[]) => void,
    error?: (error: DOMException) => void,
  ) => void;
}

interface FileSystemDirectoryEntryLike extends FileSystemEntryLike {
  createReader: () => FileSystemDirectoryReaderLike;
}

type EntryCapableDataTransferItem = {
  webkitGetAsEntry?: () => FileSystemEntryLike | null;
};

function getFileSystemEntry(item: DataTransferItem): FileSystemEntryLike | null {
  return (item as unknown as EntryCapableDataTransferItem).webkitGetAsEntry?.() || null;
}

function safeRelativePath(value: string, fallback = ''): string {
  const path = String(value || '')
    .replace(/\\/g, '/')
    .split('/')
    .filter((segment) => segment && segment !== '.' && segment !== '..')
    .join('/');
  return path || fallback;
}

function readFileEntry(entry: FileSystemFileEntryLike): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

function readDirectoryBatch(reader: FileSystemDirectoryReaderLike): Promise<FileSystemEntryLike[]> {
  return new Promise((resolve, reject) => reader.readEntries(resolve, reject));
}

async function readAllDirectoryEntries(entry: FileSystemDirectoryEntryLike): Promise<FileSystemEntryLike[]> {
  const reader = entry.createReader();
  const entries: FileSystemEntryLike[] = [];

  // Chromium returns directory entries in batches (often capped at 100), so
  // read until an empty batch marks the end of the directory.
  while (true) {
    const batch = await readDirectoryBatch(reader);
    if (batch.length === 0) break;
    entries.push(...batch);
  }

  return entries;
}

async function collectEntry(
  entry: FileSystemEntryLike,
  parentPath: string,
  payload: DroppedUploadPayload,
): Promise<void> {
  const relativePath = safeRelativePath(`${parentPath}/${entry.name}`, entry.name);

  if (entry.isFile) {
    const file = await readFileEntry(entry as FileSystemFileEntryLike);
    payload.files.push({ file, relativePath });
    return;
  }

  if (!entry.isDirectory) return;
  payload.directories.push(relativePath);
  const children = await readAllDirectoryEntries(entry as FileSystemDirectoryEntryLike);
  await Promise.all(children.map((child) => collectEntry(child, relativePath, payload)));
}

/**
 * Expands dropped folders into uploadable files while preserving their paths.
 * Falls back to the standard FileList for browsers that do not expose the
 * legacy directory-entry API.
 */
export async function collectDroppedUpload(dataTransfer: DataTransfer): Promise<DroppedUploadPayload> {
  const payload: DroppedUploadPayload = { files: [], directories: [] };
  const items = Array.from(dataTransfer.items || []);
  const fallbackFiles = Array.from(dataTransfer.files || []);

  // A browser may protect the drag data store as soon as this function yields.
  // Snapshot every entry/file synchronously before reading any entry contents;
  // otherwise a multi-file drop can lose everything after the first item.
  const droppedItems = items.map((item) => {
    const entry = getFileSystemEntry(item);
    return {
      entry,
      file: entry ? null : item.getAsFile?.() || null,
    };
  });
  const entries = droppedItems
    .map(({ entry }) => entry)
    .filter((entry): entry is FileSystemEntryLike => Boolean(entry));
  const itemFiles = droppedItems
    .map(({ file }) => file)
    .filter((file): file is File => Boolean(file));

  await Promise.all(entries.map((entry) => collectEntry(entry, '', payload)));
  payload.files.push(...itemFiles.map((file) => ({
    file,
    relativePath: safeRelativePath(file.name, file.name),
  })));

  if (entries.length === 0 && itemFiles.length === 0) {
    payload.files = fallbackFiles.map((file) => ({
      file,
      relativePath: safeRelativePath(file.webkitRelativePath || file.name, file.name),
    }));
  }

  payload.directories = [...new Set(payload.directories)]
    .sort((a, b) => a.split('/').length - b.split('/').length || a.localeCompare(b));
  payload.files.sort((a, b) => a.relativePath.localeCompare(b.relativePath));
  return payload;
}
